/**
 * AgentDetailsPage — full-page view of a single agent + all their work.
 * Feb 2026 / iter-244.
 *
 * Reached from the eye button on the "Agents" table. Shows:
 *   • Agent identity (name, email, phone, agent_id, joined date, status)
 *   • Aggregate stats (manual tasks count, registrations count, commission totals)
 *   • Manual tasks table (with status pills + row → task detail)
 *   • Company registrations table (with status pills + row → registration detail)
 *
 * Backend: GET /api/admin/agents/:agent_pk/activity
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Loader2, Shield, Mail, Phone, Calendar, KeyRound, Award,
  Briefcase, Building2, FileText, ExternalLink, Receipt as ReceiptIcon,
  CheckCircle2, XCircle, Clock, Ban, Wallet, TrendingUp, TrendingDown,
  ArrowDownToLine, RefreshCw,
} from 'lucide-react';

import DashboardHeader from '../components/DashboardHeader';
import BreadcrumbNav from '../components/BreadcrumbNav';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';
import { DirhamPrice } from '../components/DirhamPrice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_PILL = {
  pending_review: { ar: 'قيد المراجعة',   en: 'Pending',     cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30',   Icon: Clock },
  approved:       { ar: 'معتمدة',         en: 'Approved',    cls: 'bg-sky-500/15   text-sky-300   border-sky-500/30',     Icon: CheckCircle2 },
  in_progress:    { ar: 'قيد التنفيذ',     en: 'In progress', cls: 'bg-blue-500/15  text-blue-300  border-blue-500/30',   Icon: Clock },
  completed:      { ar: 'مكتملة',          en: 'Completed',   cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', Icon: CheckCircle2 },
  closed:         { ar: 'مغلقة',           en: 'Closed',      cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', Icon: CheckCircle2 },
  cancelled:      { ar: 'ملغاة',           en: 'Cancelled',   cls: 'bg-rose-500/15  text-rose-300  border-rose-500/30',    Icon: Ban },
  rejected:       { ar: 'مرفوضة',          en: 'Rejected',    cls: 'bg-rose-500/15  text-rose-300  border-rose-500/30',    Icon: XCircle },
};

function StatusPill({ status, isAr }) {
  const cfg = STATUS_PILL[status] || { ar: status || '—', en: status || '—', cls: 'bg-white/5 text-white/60 border-white/10', Icon: Clock };
  const Icon = cfg.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full border ${cfg.cls} font-semibold`}>
      <Icon className="w-3 h-3" />
      {isAr ? cfg.ar : cfg.en}
    </span>
  );
}

export default function AgentDetailsPage() {
  const { agent_id: agentPk } = useParams();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  // iter-299d (Feb 2026) — Live wallet pulled from the shared wallet endpoint.
  // Shows available / pending / lifetime earned / lifetime invoice paid for the
  // agent. Refreshed on mount + manual refresh button.
  const [wallet, setWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);

  const loadWallet = async (email) => {
    if (!email) { setWallet(null); return; }
    setWalletLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/agents/wallet-by-email/${encodeURIComponent(email)}`,
        { headers: getAuthHeaders() },
      );
      setWallet(res.data?.data || null);
    } catch (_e) {
      setWallet(null);
    } finally {
      setWalletLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/agents/${agentPk}/activity`, { headers: getAuthHeaders() });
        if (alive) {
          setData(res.data);
          const email = res.data?.agent?.email;
          if (email) loadWallet(email);
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || err.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [agentPk]);

  const agent = data?.agent || {};
  const stats = data?.stats || {};
  const tasks = data?.manual_tasks || [];
  const regs  = data?.company_registrations || [];

  return (
    <div className="min-h-screen bg-[#050510] text-white pb-16">
      <DashboardHeader showBackButton />

      <div className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'CLOUDGM FZ LLC (للإشراف)', label_en: 'CLOUDGM FZ LLC (Supervision)', path: '/dashboard?platform=fzlc' },
            { label_ar: 'الوكلاء', label_en: 'Agents', path: '/dashboard?platform=fzlc&tab=agents' },
            { label_ar: agent?.name || 'وكيل', label_en: agent?.name || 'Agent', path: null },
          ]} />
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-white/60">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
            <span>{t('جاري تحميل نشاط الوكيل…', 'Loading agent activity…')}</span>
          </div>
        ) : !data ? (
          <div className="text-center py-20 text-rose-400">{t('لم يتم العثور على بيانات', 'No data found')}</div>
        ) : (
          <>
            {/* Header identity card */}
            <motion.section
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/10 to-blue-500/5 p-6 mb-6"
              data-testid="agent-details-header"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center">
                    <Shield className="w-8 h-8 text-cyan-300" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-white">{agent.name || '—'}</h1>
                    <p className="text-xs text-cyan-400 font-mono mt-1">{agent.agent_id || agent.id}</p>
                    {agent.agent_blocked && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 mt-2 rounded-full text-[10px] bg-rose-500/15 border border-rose-500/30 text-rose-300 font-semibold">
                        <Ban className="w-3 h-3" />
                        {t('محظور', 'Blocked')}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => navigate(-1)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-white/80 text-sm"
                  data-testid="agent-details-back"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t('رجوع', 'Back')}
                </button>
              </div>

              {/* Contact grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
                <InfoTile icon={Mail}     color="#3b82f6" label={t('البريد الإلكتروني', 'Email')} value={agent.email || '—'} testid="agent-details-email" />
                {agent.phone && <InfoTile icon={Phone}   color="#10b981" label={t('رقم الجوّال', 'Phone')} value={agent.phone} dir="ltr" testid="agent-details-phone" />}
                <InfoTile icon={Calendar} color="#f59e0b" label={t('تاريخ الانضمام', 'Joined on')} value={agent.agent_approved_at ? new Date(agent.agent_approved_at).toLocaleDateString(isAr ? 'ar-SA' : 'en-US') : '—'} testid="agent-details-joined" />
                <InfoTile icon={KeyRound} color="#06b6d4" label={t('رقم العضوية', 'Membership #')} value={agent.agent_id || '—'} testid="agent-details-agent-id" />
              </div>
            </motion.section>

            {/* Stats row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <StatCard icon={Briefcase}    color="#3b82f6" label={t('مهام يدوية', 'Manual Tasks')} value={stats.manual_tasks_count || 0} testid="stat-manual-count" />
              <StatCard icon={Building2}    color="#a855f7" label={t('طلبات تسجيل شركة', 'Company Regs')} value={stats.registrations_count || 0} testid="stat-regs-count" />
              <StatCard icon={ReceiptIcon}  color="#10b981" label={t('إجمالي عمولة المهام', 'Manual Commission')} moneyValue={stats.total_commission_manual || 0} testid="stat-comm-manual" />
              <StatCard icon={Award}        color="#f59e0b" label={t('إجمالي عمولة التسجيل', 'Reg. Commission')} moneyValue={stats.total_commission_regs || 0} testid="stat-comm-regs" />
            </div>

            {/* iter-299d — Live commission wallet */}
            <section
              className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-orange-500/5 p-5 mb-8"
              data-testid="agent-details-wallet"
            >
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-amber-300" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">{t('محفظة العمولات (مباشرة)', 'Commission Wallet (Live)')}</h2>
                    <p className="text-[11px] text-amber-200/60 mt-0.5">
                      {t(
                        'المبلغ المتاح يُحسب من: إجمالي العمولات − المسحوب − المسحوبات المعلّقة − فواتير مدفوعة من المحفظة',
                        'Available = lifetime earned − withdrawn − pending withdrawals − invoices paid from wallet',
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => loadWallet(agent.email)}
                    disabled={walletLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-white/70 text-xs disabled:opacity-50"
                    data-testid="agent-wallet-refresh"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${walletLoading ? 'animate-spin' : ''}`} />
                    {t('تحديث', 'Refresh')}
                  </button>
                  <button
                    onClick={() => navigate(
                      `/dashboard/finance/cloudgm/merchants/insert-withdrawal-request?email=${encodeURIComponent(agent.email || '')}`,
                    )}
                    disabled={!agent.email}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-xs font-semibold disabled:opacity-50"
                    data-testid="agent-wallet-withdraw-btn"
                  >
                    <ArrowDownToLine className="w-3.5 h-3.5" />
                    {t('سحب من المحفظة', 'Withdraw from wallet')}
                  </button>
                </div>
              </div>

              {wallet?.negative_flag && (
                <div className="mb-3 p-2.5 rounded-lg bg-rose-500/15 border border-rose-500/30 text-rose-200 text-[12px]"
                     data-testid="agent-wallet-negative-flag">
                  {t(
                    'تنبيه: الحسابات أدناه تُشير إلى رصيد سالب. تمّت إعادة ضبط المبلغ المتاح إلى صفر — راجع السجل.',
                    'Warning: ledger shows a negative balance. Available was clamped to zero — please review entries.',
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <WalletTile
                  icon={Wallet}
                  color="#fbbf24"
                  label={t('المتاح للسحب', 'Available')}
                  value={wallet?.available}
                  loading={walletLoading}
                  testid="wallet-available"
                  emphasis
                />
                <WalletTile
                  icon={Clock}
                  color="#f59e0b"
                  label={t('مسحوبات معلّقة', 'Pending Withdrawals')}
                  value={wallet?.pending_withdrawals}
                  loading={walletLoading}
                  testid="wallet-pending"
                />
                <WalletTile
                  icon={TrendingUp}
                  color="#10b981"
                  label={t('إجمالي العمولات المكتسبة', 'Lifetime Earned')}
                  value={wallet?.lifetime_earned}
                  loading={walletLoading}
                  testid="wallet-lifetime-earned"
                />
                <WalletTile
                  icon={TrendingDown}
                  color="#64748b"
                  label={t('مسحوب من المحفظة لفواتير', 'Paid to ASB606')}
                  value={wallet?.lifetime_invoice_paid}
                  loading={walletLoading}
                  testid="wallet-lifetime-invoice-paid"
                />
              </div>
            </section>

            {/* Manual tasks */}
            <section className="mb-8">
              <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-sky-400" />
                {t('مهام الوكيل اليدوي', 'Manual Agent Tasks')}
                <span className="text-xs text-white/40 font-mono">({tasks.length})</span>
              </h2>
              {tasks.length === 0 ? (
                <EmptyState msg={t('لا توجد مهام لهذا الوكيل', 'No manual tasks for this agent')} />
              ) : (
                <div className="overflow-x-auto rounded-xl border border-white/10">
                  <table className="min-w-full text-sm">
                    <thead className="bg-white/[0.02] border-b border-white/10">
                      <tr>
                        <Th>{t('#', '#')}</Th>
                        <Th>{t('رقم المهمة', 'Task ID')}</Th>
                        <Th>{t('الخدمة', 'Service')}</Th>
                        <Th>{t('العميل', 'Customer')}</Th>
                        <Th>{t('الحالة', 'Status')}</Th>
                        <Th className="text-end">{t('الإجمالي', 'Total')}</Th>
                        <Th className="text-end">{t('العمولة', 'Commission')}</Th>
                        <Th>{t('التاريخ', 'Date')}</Th>
                        <Th className="text-center">{t('فتح', 'Open')}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.map((task, idx) => (
                        <tr key={task.id || task.task_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`agent-task-row-${idx}`}>
                          <Td className="text-white/40">{idx + 1}</Td>
                          <Td className="font-mono text-cyan-300 text-xs">{task.task_id || '—'}</Td>
                          <Td>{isAr ? (task.service_name_ar || task.service_name_en) : (task.service_name_en || task.service_name_ar)}</Td>
                          <Td>{task.customer_name || '—'}</Td>
                          <Td><StatusPill status={task.status} isAr={isAr} /></Td>
                          <Td className="text-end font-mono"><DirhamPrice value={task.total_price || 0} /></Td>
                          <Td className="text-end font-mono text-emerald-300"><DirhamPrice value={task.commission_amount || 0} /></Td>
                          <Td className="text-white/50 text-xs">{task.created_at ? new Date(task.created_at).toLocaleDateString(isAr ? 'ar-SA' : 'en-US') : '—'}</Td>
                          <Td className="text-center">
                            <button
                              onClick={() => navigate(`/manage/cloudgm-agent/task/${task.task_id || task.id}`)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/20 text-xs"
                              title={t('فتح المهمة', 'Open task')}
                              data-testid={`agent-task-open-${idx}`}
                            >
                              <ExternalLink className="w-3 h-3" />
                            </button>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Company registrations */}
            <section>
              <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-purple-400" />
                {t('طلبات تسجيل الشركات', 'Company Registration Requests')}
                <span className="text-xs text-white/40 font-mono">({regs.length})</span>
              </h2>
              {regs.length === 0 ? (
                <EmptyState msg={t('لا توجد طلبات تسجيل شركة لهذا الوكيل', 'No company registrations for this agent')} />
              ) : (
                <div className="overflow-x-auto rounded-xl border border-white/10">
                  <table className="min-w-full text-sm">
                    <thead className="bg-white/[0.02] border-b border-white/10">
                      <tr>
                        <Th>{t('#', '#')}</Th>
                        <Th>{t('رقم الطلب', 'Request Code')}</Th>
                        <Th>{t('الشركة', 'Company')}</Th>
                        <Th>{t('العميل', 'Client')}</Th>
                        <Th>{t('الحالة', 'Status')}</Th>
                        <Th className="text-end">{t('رسوم الرخصة', 'License Fees')}</Th>
                        <Th className="text-end">{t('عمولة الوكيل', 'Agent Comm.')}</Th>
                        <Th>{t('التاريخ', 'Date')}</Th>
                        <Th className="text-center">{t('فتح', 'Open')}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {regs.map((reg, idx) => {
                        const tl = reg.trade_license || {};
                        const compName = isAr ? (tl.company_name_ar || tl.company_name_en) : (tl.company_name_en || tl.company_name_ar);
                        const client = reg.client || {};
                        const licAmt = Number(reg.license_fees?.amount || 0);
                        const commAmt = Number(reg.agent?.commission_amount || 0);
                        return (
                          <tr key={reg.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`agent-reg-row-${idx}`}>
                            <Td className="text-white/40">{idx + 1}</Td>
                            <Td className="font-mono text-purple-300 text-xs">{reg.request_code || reg.code || '—'}</Td>
                            <Td>{compName || '—'}</Td>
                            <Td>{client.name || '—'}</Td>
                            <Td><StatusPill status={reg.status} isAr={isAr} /></Td>
                            <Td className="text-end font-mono"><DirhamPrice value={licAmt} /></Td>
                            <Td className="text-end font-mono text-emerald-300"><DirhamPrice value={commAmt} /></Td>
                            <Td className="text-white/50 text-xs">{reg.created_at ? new Date(reg.created_at).toLocaleDateString(isAr ? 'ar-SA' : 'en-US') : '—'}</Td>
                            <Td className="text-center">
                              <button
                                onClick={() => navigate(`/manage/cloudgm-agent/company-registration/track/${reg.id}`)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-purple-500/10 border border-purple-500/30 text-purple-300 hover:bg-purple-500/20 text-xs"
                                title={t('فتح الطلب', 'Open registration')}
                                data-testid={`agent-reg-open-${idx}`}
                              >
                                <ExternalLink className="w-3 h-3" />
                              </button>
                            </Td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

/* ─── Sub-components ─────────────────────────────────────────────────── */

function InfoTile({ icon: Icon, color, label, value, dir, testid }) {
  return (
    <div className="px-3 py-2.5 bg-white/[0.03] border border-white/10 rounded-lg flex items-center gap-2.5" data-testid={testid}>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}20`, borderColor: `${color}40` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] text-[#606060] uppercase tracking-wide">{label}</div>
        <div className="text-white text-sm truncate" dir={dir}>{value}</div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value, moneyValue, testid }) {
  return (
    <div className="p-4 rounded-xl border border-white/10 bg-white/[0.02] flex items-center gap-3" data-testid={testid}>
      <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}20` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] text-white/50">{label}</div>
        <div className="text-white text-lg font-bold mt-0.5">
          {moneyValue !== undefined ? <DirhamPrice value={moneyValue} /> : value}
        </div>
      </div>
    </div>
  );
}

function WalletTile({ icon: Icon, color, label, value, loading, testid, emphasis }) {
  const borderCls = emphasis ? 'border-amber-500/40' : 'border-white/10';
  const bgCls = emphasis ? 'bg-amber-500/10' : 'bg-white/[0.03]';
  return (
    <div
      className={`p-3.5 rounded-xl border ${borderCls} ${bgCls} flex items-center gap-3`}
      data-testid={testid}
    >
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${color}20`, border: `1px solid ${color}40` }}
      >
        <Icon className="w-4.5 h-4.5" style={{ color }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] text-white/50 uppercase tracking-wide">{label}</div>
        <div className={`${emphasis ? 'text-amber-100 text-xl' : 'text-white text-base'} font-bold mt-0.5`}>
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin inline" />
          ) : value == null ? (
            <span className="text-white/30">—</span>
          ) : (
            <DirhamPrice value={value} />
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ msg }) {
  return (
    <div className="p-8 rounded-xl border border-dashed border-white/10 bg-white/[0.02] text-center text-white/50">
      <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
      {msg}
    </div>
  );
}

function Th({ children, className = '' }) {
  return <th className={`px-3 py-2.5 text-start text-[11px] font-semibold text-white/60 uppercase tracking-wide ${className}`}>{children}</th>;
}

function Td({ children, className = '' }) {
  return <td className={`px-3 py-2.5 text-white/90 align-middle ${className}`}>{children}</td>;
}
